import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { componentDataTagId } from '../constants';

function roundToTwo(value) {
  return Math.round(value * 100) / 100;
}

/**
 * Build a reverse-lookup map: tagId (string) → [{ node, property }]
 * This is computed once at module load time from the static template.
 */
const TAG_INDEX = (() => {
  const index = new Map();
  Object.keys(componentDataTagId).forEach((node) => {
    const nodeData = componentDataTagId[node];
    if (!nodeData) return;
    Object.keys(nodeData).forEach((property) => {
      const tagid = String(nodeData[property]?.tagid);
      if (!tagid) return;
      if (!index.has(tagid)) index.set(tagid, []);
      index.get(tagid).push({ node, property });
    });
  });
  return index;
})();

/**
 * Builds componentData by scanning livedata once with an O(1) Map lookup per item.
 * The result object structure matches the original deep-clone approach so all
 * consumers (DTWidgetCard, dt-special widgets) continue to work unchanged.
 */
function buildComponentData(livedata) {
  // Shallow-clone the top-level nodes; each node value starts as the static template.
  // We only deep-clone nodes that actually receive a live update.
  const data = {};
  Object.keys(componentDataTagId).forEach((node) => {
    data[node] = { ...componentDataTagId[node] };
  });

  for (let i = 0; i < livedata.length; i++) {
    const item = livedata[i];
    const tagid = String(item.TagId);
    const targets = TAG_INDEX.get(tagid);
    if (!targets) continue;
    const value = roundToTwo(parseFloat(item.value));
    targets.forEach(({ node, property }) => {
      // Clone the property object only on first write to this node
      if (data[node][property] === componentDataTagId[node][property]) {
        data[node][property] = { ...componentDataTagId[node][property] };
      }
      data[node][property].data = value;
    });
  }

  return data;
}

export function useComponentData() {
  const livedata = useSelector((state) => state.mainpagedata.livedata);

  // useMemo instead of useEffect+setState: avoids the extra render cycle and
  // only recomputes when livedata reference changes (i.e. on actual live updates).
  const componentData = useMemo(() => {
    if (!livedata || livedata.length === 0) return {};
    return buildComponentData(livedata);
  }, [livedata]);

  return componentData;
}
